import React from 'react';

import find from 'lodash/find';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {AttachOverlay, type OverlayPlacementType} from 'app/general/overlays';
import {SimpleItemPickerListPopoverRTK} from 'app/general/widgets/SimpleItemPickerList';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import type {UseQueryType} from 'app/general/datafetchers/hooks/useInfiniteListFetcherRTK';
import type {
  DetailPolicySerializedType,
  FolderSerializedType,
  ProcedureSerializedType,
  ProcessSerializedType,
  QuizSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type GenericDocType =
  | QuizSerializedType
  | DetailPolicySerializedType
  | FolderSerializedType
  | ProcedureSerializedType
  | ProcessSerializedType;

type AddActionType = {
  team: TeamSerializedType;
  urls: Array<string>;
};

type GenericFetchParamType = {
  page: string;
};

type Props<T extends GenericDocType, FP extends GenericFetchParamType> = {
  team: TeamSerializedType;
  contentType: 'procedure' | 'policy' | 'quiz' | 'proceduregroup' | 'folder';
  buttonClass?: string;
  placement?: OverlayPlacementType;
  useDataHook: UseQueryType<FP, T>;
  paramsConverter: (query: Record<string, any>) => FP;
  addAction: (params: AddActionType) => Promise<unknown>;
  pageParam?: string;
};
export function AddNewTeamDocButton<
  DocType extends GenericDocType,
  FP extends GenericFetchParamType
>({
  team,
  contentType,
  buttonClass = 'btn btn-light',
  placement = 'left',
  useDataHook,
  paramsConverter,
  pageParam,
  addAction,
}: Props<DocType, FP>) {
  const ct = getContentType(contentType);

  const addDocsToTeam = (docs: Array<DocType>) => {
    const urls = docs.map((doc) => doc.url); // Adding the document urls in an array needed for bulk upload
    addAction({team, urls});
  };

  const getUserItemProps = (doc: DocType) => {
    return {
      disabledText:
        doc.can_edit === true
          ? find(doc.team_memberships, {
              team: team.url,
            }) != null
            ? gt('Already in team')
            : undefined
          : gt(`You must be a manager of this ${ct.name}`),
    };
  };

  return (
    <AttachOverlay
      trigger="click"
      overlay={({overlayProps}) => (
        <SimpleItemPickerListPopoverRTK
          showDescription={false}
          overlayProps={{...overlayProps, placement}}
          additionalParams={{
            ordering: 'name',
            exclude_team_id: team.id,
          }}
          pageParam={pageParam}
          useDataHook={useDataHook}
          paramsConverter={paramsConverter}
          addItemProps={getUserItemProps}
          onConfirm={addDocsToTeam}
          searchPlaceholder={gt(`Search all ${ct.pluralName}`)}
          isPopover={true}
          isSmall={true}
        />
      )}
    >
      {(triggers) => (
        <LazyTooltip hide={team.can_edit} tooltip={gt(`You must be a manager`)}>
          <div>
            <button
              disabled={!team.can_edit}
              className={buttonClass}
              {...triggers}
            >
              <i className="fas fa-plus-circle mr-1" />
              {gt(`Add ${ct.name}`)}
            </button>
          </div>
        </LazyTooltip>
      )}
    </AttachOverlay>
  );
}
